import { Link } from "react-router-dom";
import { MdRemoveCircleOutline } from "react-icons/md";
import {
  cleanSymbol,
  shortName,
  formatPrice,
  formatDayChange,
  dayChangeColor,
  chartSymbolFor,
  ZERO,
} from "./watchlistFormat";

// One asset row for the main table. `variant` = "user" | "curated".
// In edit mode the row does not navigate; price/day-change dim and a red
// remove control appears at the far right.
//
// `gate` comes from useLeagueAssetGate and decides where the row opens — the
// main chart, this league's Trade screen, or nowhere with "Not eligible in this
// league". It is optional: without one the row behaves exactly as it always has,
// which is what keeps every non-league caller unchanged.
export default function AssetRow({ item, variant, editMode, onRemove, gate }) {
  const isCurated = variant === "curated";
  const symbol = cleanSymbol(item?.symbol);
  const priceText = formatPrice(item?.details?.latestData?.c);
  const changeRaw = item?.details?.percentageChange;
  const changeText = formatDayChange(changeRaw);

  // Leading visual: curated rows use the list logo; user rows use a teal chip.
  const leading = isCurated ? (
    <img
      src={item?.logo || "/bitcoin.png"}
      alt=""
      className="h-10 w-10 shrink-0 rounded-full object-contain"
    />
  ) : (
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primaryColor">
      <span className="truncate px-0.5 text-[10px] font-semibold leading-tight text-white">
        {symbol}
      </span>
    </div>
  );

  // Asset cell: chip/logo + name (curated adds a description line).
  const assetCell = (
    <div className="flex min-w-0 flex-1 items-center gap-3">
      {leading}
      <div className="min-w-0">
        {isCurated ? (
          <>
            <div
              className="truncate font-montserrat text-[15px] font-semibold text-black"
              title={item?.watchlist_name || ""}
            >
              {item?.watchlist_name || symbol || "N/A"}
            </div>
            {item?.description ? (
              <div className="truncate text-[13px] text-secondary" title={item.description}>
                {item.description}
              </div>
            ) : null}
          </>
        ) : (
          <div
            className="truncate font-montserrat text-[15px] text-black"
            title={item?.details?.stock_name || ""}
          >
            {shortName(item?.details?.stock_name)}
          </div>
        )}
      </div>
    </div>
  );

  const dim = editMode ? "opacity-40" : "";

  const metrics = (
    <>
      <div className={`w-28 shrink-0 text-right text-sm font-semibold text-black ${dim}`}>
        {priceText ?? <span className="text-tertiary">—</span>}
      </div>
      <div
        className={`w-24 shrink-0 text-right text-sm font-semibold ${dim}`}
        style={{ color: changeText ? dayChangeColor(changeRaw) : ZERO }}
      >
        {changeText ?? "—"}
      </div>
    </>
  );

  const inner = (
    <div className="flex items-center gap-4">
      {assetCell}
      {metrics}
      {editMode && (
        <div className="flex w-8 shrink-0 justify-end">
          <button
            type="button"
            aria-label={`Remove ${symbol}`}
            onClick={() => onRemove?.(item)}
            className="text-red-500 transition-colors hover:text-red-600"
          >
            <MdRemoveCircleOutline size={24} />
          </button>
        </div>
      )}
    </div>
  );

  if (editMode) {
    return <div className="rounded-lg px-2 py-3">{inner}</div>;
  }

  const href = gate ? gate.linkFor(item) : `/chartSearch/${chartSymbolFor(item)}`;

  // An ineligible row inside a league still renders — client: "Nothing is
  // visually filtered out of the watchlist itself." It just stops being a link,
  // and says why when tapped.
  if (!href) {
    return (
      <button
        type="button"
        onClick={() => gate.onOpen(item)}
        className="block w-full rounded-lg px-2 py-3 text-left transition-colors hover:bg-primaryColor/5"
      >
        {inner}
      </button>
    );
  }

  return (
    <Link
      to={href}
      onClick={(e) => {
        if (gate && !gate.onOpen(item)) e.preventDefault();
        else if (!gate) localStorage.setItem("ticker", chartSymbolFor(item));
      }}
      className="block rounded-lg px-2 py-3 transition-colors hover:bg-primaryColor/5"
    >
      {inner}
    </Link>
  );
}
